import { get } from "lodash";
import React, { FC, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Col } from "reactstrap";
import PromptModal from "src/components/ConfirmationModal/PromptModalV2";
import { IRootState } from "src/store/reducers";
import {
  addRenewalProduct,
  fetchCommissionRenewals,
  fetchNewRenewalDTO,
  updateAllProducts,
} from "src/store/reducers/brokerCommissions";
import { sanitizePercent } from "src/util/util.sanitizePercetange";
import DisTable from "../../../components/TableV2/Table";
import * as S from "../../../constants/StringConstants";

const rowIdOf = r => r?._localId ?? r?.renewalID ?? r?.productID;

const YEAR_FIELDS = [
  { key: "year1Percentage", title: "Year 1" },
  { key: "year2Percentage", title: "Year 2" },
  { key: "year3Percentage", title: "Year 3" },
  { key: "year4Percentage", title: "Year 4" },
  { key: "year5Percentage", title: "Year 5" },
  { key: "year6Percentage", title: "Year 6" },
  { key: "year7Percentage", title: "Year 7" },
  { key: "year8Percentage", title: "Year 8" },
  { key: "year9Percentage", title: "Year 9" },
  { key: "year10Percentage", title: "Year 10" },
  { key: "year11Percentage", title: "Year 11+" },
];

interface CommissionTableModalProps {
  open: boolean;
  onClose: () => void;
  commissionId: number | null;
  brokerId: string | number;
  carrierId: number;
}

const CommissionTableModal: FC<CommissionTableModalProps> = ({ open, onClose, commissionId, brokerId, carrierId }) => {
  const tableRef = useRef(null);
  const lastFetchedRef = useRef([]);
  const fetchedRowsRef = useRef([]);
  const currentCommissionIdRef = useRef(null);
  const [shouldFetch, setShouldFetch] = useState(true);

  const dispatch = useDispatch();
  const [isEditing, setIsEditing] = useState(false);
  const [isAdding, setIsAdding] = useState(false);
  const [edits, setEdits] = useState({});
  const [newRows, setNewRows] = useState([]);
  const { selectProductTypeList } = useSelector((state: IRootState) => state.brokerCommissions);

  const filteredProductTypeList = useMemo(() => {
    if (!carrierId) return selectProductTypeList || [];
    return (selectProductTypeList || []).filter(p => p.carrierID === carrierId);
  }, [selectProductTypeList, carrierId]);

  useEffect(() => {
    if (open && commissionId != null) {
      tableRef.current?.update?.();
    }
  }, [isAdding, newRows, open, commissionId]);

  const startEditing = () => {
    const src = lastFetchedRef.current || [];
    const next = {};
    for (const r of src) {
      const rid = rowIdOf(r);
      if (rid == null) continue;
      next[rid] = {};
      for (const { key } of YEAR_FIELDS) next[rid][key] = String(r[key] ?? "");
    }
    setEdits(next);
    setIsEditing(true);
  };

  const cancelEditing = () => {
    setIsEditing(false);
    setEdits({});
  };

  const handleCellChange = (rowId, fieldKey, raw) => {
    const valueStr = sanitizePercent(raw);
    setEdits(prev => ({
      ...prev,
      [rowId]: { ...(prev[rowId] || {}), [fieldKey]: valueStr },
    }));
  };

  const saveEditing = async () => {
    if (commissionId == null) return;

    const merged = lastFetchedRef.current.map(row => {
      const rid = rowIdOf(row);
      const rowEdits = edits[rid] || {};
      const out = { ...row };
      for (const { key } of YEAR_FIELDS) {
        const raw = rowEdits[key];
        if (raw !== undefined) {
          out[key] = raw === "" ? row[key] : Number(raw);
        }
      }
      return out;
    });

    const changed = merged.filter((out, idx) => {
      const orig = lastFetchedRef.current[idx];
      return YEAR_FIELDS.some(({ key }) => Number(out[key]) !== Number(orig[key]));
    });

    if (changed.length === 0) {
      setIsEditing(false);
      setEdits({});
      return;
    }

    try {
      await dispatch(updateAllProducts(brokerId, commissionId, changed));
      setIsEditing(false);
      setEdits({});
      setShouldFetch(true);
      tableRef.current?.update?.();
    } catch (e) {}
  };

  const startAdding = () => {
    const emptyRow = {
      _localId: `new-${Date.now()}`,
      _isNew: true,
      productID: "",
      productName: "",
    };
    for (const { key } of YEAR_FIELDS) emptyRow[key] = "";
    setNewRows([emptyRow]);
    setIsAdding(true);
  };

  const cancelAdding = () => {
    setIsAdding(false);
    setNewRows([]);
  };

  const handleNewRowChange = (rowId, fieldKey, raw) => {
    setNewRows(prev =>
      prev.map(r => {
        const rid = rowIdOf(r);
        if (rid !== rowId) return r;
        if (fieldKey === "productID") {
          return { ...r, [fieldKey]: raw };
        }
        return { ...r, [fieldKey]: sanitizePercent(raw) };
      }),
    );
  };

  const saveAdding = async () => {
    if (!commissionId) return;
    const row = newRows[0];
    if (!row || !row.productID) return;

    const payload = {
      productID: Number(row.productID),
      renewalID: 0,
      year1Percentage: Number(row.year1Percentage || 0),
      year2Percentage: Number(row.year2Percentage || 0),
      year3Percentage: Number(row.year3Percentage || 0),
      year4Percentage: Number(row.year4Percentage || 0),
      year5Percentage: Number(row.year5Percentage || 0),
      year6Percentage: Number(row.year6Percentage || 0),
      year7Percentage: Number(row.year7Percentage || 0),
      year8Percentage: Number(row.year8Percentage || 0),
      year9Percentage: Number(row.year9Percentage || 0),
      year10Percentage: Number(row.year10Percentage || 0),
      year11Percentage: Number(row.year11Percentage || 0),
    };

    try {
      await dispatch(addRenewalProduct(brokerId, commissionId, payload));
      setIsAdding(false);
      setNewRows([]);
      setShouldFetch(true);
      tableRef.current?.update?.();
    } catch (e) {}
  };

  useEffect(() => {
    dispatch(fetchNewRenewalDTO(brokerId));
  }, []);

  const columns = [
    {
      key: "product",
      title: S.BRO_COMMISSIONS_COMMISSION_TABLE_PRODUCT_NAME,
      style: { textAlign: "left", minWidth: "15rem" },
      render: r => {
        const rid = rowIdOf(r);
        if (r._isNew) {
          return (
            <select
              value={r.productID ?? ""}
              onChange={e => handleNewRowChange(rid, "productID", e.target.value)}
              className="single-select-field-v2__field-input">
              <option value="">Select product</option>
              {(filteredProductTypeList || []).map(p => (
                <option key={p.planID} value={p.planID}>
                  {p.planTypeDesc} — {p.typeDesc} (Carrier {p.carrierID})
                </option>
              ))}
            </select>
          );
        }
        return <>{filteredProductTypeList.find(p => p.planID === r.productID)?.planTypeDesc}</>;
      },
    },

    ...YEAR_FIELDS.map(({ key, title }) => ({
      key,
      title,
      style: { minWidth: "8rem" },
      render: r => {
        const rid = rowIdOf(r);
        if (r._isNew) {
          return (
            <input
              className="broker-commissions__percentage-input"
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              value={r[key] ?? ""}
              onChange={e => handleNewRowChange(rid, key, e.target.value)}
            />
          );
        }
        if (isEditing) {
          return (
            <input
              className="broker-commissions__percentage-input"
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              value={edits[rid]?.[key] ?? ""}
              onChange={e => handleCellChange(rid, key, e.target.value)}
            />
          );
        }
        return `${r[key]}%`;
      },
    })),
  ];

  return (
    <PromptModal
      open={open}
      rejectText="Close"
      onReject={() => {
        setIsAdding(false);
        setNewRows([]);
        onClose();
      }}
      width="120rem">
      <div className="renewal-table__title-container">
        <h1 className="form-title">{S.BRO_COMMISSIONS_COMMISSION_TABLE_TITLE}</h1>

        <div className="renewal-table__actions-container" style={{ display: "flex", gap: 12 }}>
          {!isEditing && !isAdding ? (
            <>
              <button className="button-v2 button-v2__secondary" onClick={startEditing}>
                {S.BRO_COMMISSIONS_COMMISSION_TABLE_UPDATE_PRODUCT}
              </button>
              <button className="button-v2 button-v2__primary" onClick={startAdding}>
                {S.BRO_COMMISSIONS_COMMISSION_TABLE_ADD}
              </button>
            </>
          ) : null}

          {isEditing && (
            <>
              <button className="button-v2 button-v2__secondary" onClick={cancelEditing}>
                {S.BRO_COMMISSIONS_COMMISSION_TABLE_CANCEL}
              </button>
              <button className="button-v2 button-v2__primary" onClick={saveEditing}>
                {S.BRO_COMMISSIONS_COMMISSION_TABLE_SAVE}
              </button>
            </>
          )}

          {isAdding && (
            <>
              <button className="button-v2 button-v2__secondary" onClick={cancelAdding}>
                {S.BRO_COMMISSIONS_COMMISSION_TABLE_CANCEL}
              </button>
              <button className="button-v2 button-v2__primary" onClick={saveAdding}>
                {S.BRO_COMMISSIONS_COMMISSION_TABLE_SAVE}
              </button>
            </>
          )}
        </div>
      </div>

      <Col className="doc-tab__table-override">
        <DisTable
          isPaginated={false}
          columns={columns}
          initialState={{}}
          getRef={ft => (tableRef.current = ft)}
          onUpdate={async () => {
            const commissionChanged = currentCommissionIdRef.current !== commissionId;

            if (shouldFetch || commissionChanged) {
              const result = await dispatch(fetchCommissionRenewals(brokerId, commissionId));
              const fetched = get(result, "value.data");
              fetchedRowsRef.current = fetched || [];
              lastFetchedRef.current = fetched || [];
              currentCommissionIdRef.current = commissionId;
              setShouldFetch(false);
            }

            const base = fetchedRowsRef.current || [];
            const data = isAdding || newRows.length ? [...base, ...newRows] : base;

            return { total: data.length, data };
          }}
        />
      </Col>
    </PromptModal>
  );
};

export default CommissionTableModal;
